import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Users as UsersIcon,
  DollarSign,
  TrendingUp,
  AlertCircle,
  RefreshCw,
  ShieldCheck,
  Loader2,
  Sparkles,
  MessageSquare,
  CheckCircle2,
  Save,
} from "lucide-react";
import WelcomeBanner from "../../components/WelcomeBanner";
import PlanBadge from "../../components/PlanBadge";
import { useAuth, apiClient, formatApiError } from "../../lib/auth";

export default function Admin() {
  const { user, hydrated, refresh } = useAuth();
  const [data, setData] = useState(null);
  const [usage, setUsage] = useState(null);
  const [msg, setMsg] = useState(null);
  const [msgSid, setMsgSid] = useState("");
  const [msgBusy, setMsgBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyUserId, setBusyUserId] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get("/admin/overview");
      setData(res.data);
      try { const u = await apiClient.get("/admin/usage"); setUsage(u.data); } catch (_e) { /* non-fatal */ }
      try { const m = await apiClient.get("/admin/messaging-config"); setMsg(m.data); setMsgSid(m.data.messaging_service_sid || ""); } catch (_e) { /* non-fatal */ }
    } catch (e) {
      setError(formatApiError(e, "Could not load admin overview."));
    } finally {
      setLoading(false);
    }
  };

  const saveMessaging = async (patch) => {
    setMsgBusy(true);
    try {
      const { data: d } = await apiClient.post("/admin/messaging-config", patch);
      setMsg((m) => ({ ...(m || {}), ...d }));
      if (d.messaging_service_sid !== undefined) setMsgSid(d.messaging_service_sid);
    } catch (e) { setError(formatApiError(e, "Could not update messaging config.")); }
    finally { setMsgBusy(false); }
  };

  const changePlan = async (userId, plan, planId) => {
    setBusyUserId(userId);
    setError(null);
    try {
      const body = { plan };
      if (planId) body.plan_id = planId;
      await apiClient.post(`/admin/users/${userId}/plan`, body);
      if (userId === user.user_id) await refresh();
      await load();
    } catch (e) {
      setError(formatApiError(e, "Could not change plan."));
    } finally {
      setBusyUserId(null);
    }
  };

  useEffect(() => { if (user?.is_admin) load(); /* eslint-disable-line */ }, [user?.is_admin]);

  // Frontend-side gate — backend already returns 403 for non-admins
  if (!hydrated) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/dashboard/profile" replace />;

  return (
    <div className="space-y-7" data-testid="admin-page">
      <WelcomeBanner
        eyebrow="Admin"
        title="Operations Overview"
        subtitle="Signups, MRR, active subs, and recent activity across Newground."
      />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 flex items-start gap-3" data-testid="admin-error">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> <span>{error}</span>
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5">
          <ShieldCheck className="w-3.5 h-3.5" /> Admin access · {user.email}
        </div>
        <button
          data-testid="admin-refresh"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 text-sm bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white font-semibold rounded-xl px-4 py-2 transition-all"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Refresh
        </button>
      </div>

      {loading && !data && (
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center text-slate-500 text-sm" data-testid="admin-loading">
          Loading metrics…
        </div>
      )}

      {data && (
        <>
          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm" data-testid="self-plan-switcher">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1">Preview as</div>
                <h3 className="font-display text-lg font-semibold text-slate-900">
                  Switch your own plan instantly
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Use this to QA the Free / Pro / Elite dashboard experiences without going through Stripe.
                </p>
              </div>
              <div className="flex items-center gap-2">
                {["free", "pro", "elite"].map((p) => (
                  <button
                    key={p}
                    data-testid={`self-switch-${p}`}
                    onClick={() => changePlan(user.user_id, p)}
                    disabled={busyUserId === user.user_id}
                    className={`text-sm font-semibold rounded-xl px-4 py-2 transition-all border ${
                      user.plan === p
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                    } ${busyUserId === user.user_id ? "opacity-60" : ""}`}
                  >
                    {p === "free" ? "Free" : p === "pro" ? "Pro" : "Elite"}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              testId="metric-mrr"
              icon={DollarSign}
              label="MRR (current)"
              value={`$${data.mrr_usd.toLocaleString(undefined, { minimumFractionDigits: 0 })}`}
              accent="from-emerald-50 to-white"
              iconBg="bg-emerald-100 text-emerald-700"
            />
            <MetricCard
              testId="metric-total-users"
              icon={UsersIcon}
              label="Total users"
              value={data.total_users}
              accent="from-blue-50 to-white"
              iconBg="bg-blue-100 text-blue-700"
            />
            <MetricCard
              testId="metric-active-subs"
              icon={TrendingUp}
              label="Active subscriptions"
              value={data.active_subscriptions}
              accent="from-amber-50 to-white"
              iconBg="bg-amber-100 text-amber-700"
            />
            <MetricCard
              testId="metric-cancellations"
              icon={AlertCircle}
              label="Pending cancellations"
              value={data.pending_cancellations}
              accent="from-rose-50 to-white"
              iconBg="bg-rose-100 text-rose-700"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <PlanBucket label="Free" value={data.free_count} accent="bg-slate-100 text-slate-700" testId="bucket-free" />
            <PlanBucket label="Pro" value={data.pro_count} accent="bg-blue-100 text-blue-700" testId="bucket-pro" />
            <PlanBucket label="Elite" value={data.elite_count} accent="bg-amber-100 text-amber-700" testId="bucket-elite" />
          </div>

          <Panel title="Messaging readiness (A2P 10DLC)" testId="admin-messaging-config">
            {(() => {
              const live = msg?.a2p_status === "live";
              return (
                <div className="space-y-4">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3">
                      <div className={`w-11 h-11 rounded-xl border flex items-center justify-center ${live ? "bg-emerald-50 border-emerald-200 text-emerald-600" : "bg-amber-50 border-amber-200 text-amber-600"}`}><MessageSquare className="w-5 h-5" /></div>
                      <div>
                        <div className="text-sm font-semibold text-slate-900">Texting status</div>
                        <div className="text-xs text-slate-500">Controls the "texting activates shortly" note agents see.</div>
                      </div>
                    </div>
                    <span data-testid="a2p-status-pill" className={`inline-flex items-center gap-1.5 text-xs font-bold rounded-full px-3 py-1 border ${live ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                      {live ? <><CheckCircle2 className="w-3.5 h-3.5" /> Live</> : <><Loader2 className="w-3.5 h-3.5" /> Pending A2P</>}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button data-testid="a2p-set-live" disabled={msgBusy || live} onClick={() => saveMessaging({ a2p_status: "live" })}
                      className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white text-sm font-semibold rounded-xl px-4 py-2 transition-all">
                      {msgBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Mark texting Live
                    </button>
                    <button data-testid="a2p-set-pending" disabled={msgBusy || !live} onClick={() => saveMessaging({ a2p_status: "pending" })}
                      className="inline-flex items-center gap-1.5 border border-slate-200 hover:bg-slate-50 disabled:opacity-50 text-slate-700 text-sm font-semibold rounded-xl px-4 py-2 transition-all">
                      Set to Pending
                    </button>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wider">A2P Messaging Service SID</label>
                    <div className="flex gap-2">
                      <input data-testid="messaging-sid-input" value={msgSid} onChange={(e) => setMsgSid(e.target.value)} placeholder="MGxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                        className="flex-1 bg-slate-50 border border-slate-200 focus:border-[#1D6AFF] focus:bg-white outline-none rounded-xl px-3.5 py-2.5 text-sm font-mono transition-all" />
                      <button data-testid="messaging-sid-save" disabled={msgBusy} onClick={() => saveMessaging({ messaging_service_sid: msgSid })}
                        className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white text-sm font-semibold rounded-xl px-4 py-2.5 transition-all">
                        {msgBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
                      </button>
                    </div>
                    <p className="text-[11px] text-slate-400 mt-1.5">
                      Paste your Twilio Messaging Service SID (from your approved A2P campaign). New agent numbers are auto-attached to it on purchase.
                      {msg ? ` · ${msg.agents_attached}/${msg.agents_total} agent numbers attached.` : ""}
                    </p>
                  </div>
                </div>
              );
            })()}
          </Panel>

          <Panel
            title={`Agent usage this month${usage ? ` · $${usage.total_spend_usd.toFixed(2)} across ${usage.agent_count} agent${usage.agent_count === 1 ? "" : "s"}` : ""}`}
            testId="admin-usage">
            {!usage || usage.agents.length === 0 ? (
              <Empty>No agents have activated a calling number yet.</Empty>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
                    <th className="text-left py-2 pl-1 font-semibold">Agent</th>
                    <th className="text-left py-2 font-semibold">Number</th>
                    <th className="text-left py-2 font-semibold w-[38%]">Spend / Cap</th>
                    <th className="text-right py-2 pr-1 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {usage.agents.map((a) => (
                    <tr key={a.user_id} className="border-b border-slate-100 last:border-0" data-testid={`usage-row-${a.user_id}`}>
                      <td className="py-2.5 pl-1 max-w-[180px]">
                        <div className="font-medium text-slate-900 truncate">{a.name}</div>
                        <div className="text-xs text-slate-500 truncate">{a.email}</div>
                      </td>
                      <td className="py-2.5 text-xs text-slate-600 whitespace-nowrap">{a.smart_number}</td>
                      <td className="py-2.5 pr-4">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="tabular-nums text-slate-700 font-semibold">${a.used.toFixed(2)}</span>
                          <span className="tabular-nums text-slate-400">of ${a.cap.toFixed(0)}</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                          <div className={`h-full rounded-full ${a.status === "at_limit" ? "bg-red-500" : a.status === "near_limit" ? "bg-amber-500" : "bg-emerald-500"}`}
                            style={{ width: `${Math.min(100, a.pct * 100)}%` }} />
                        </div>
                      </td>
                      <td className="py-2.5 pr-1 text-right">
                        <span className={`inline-flex items-center gap-1 text-[11px] font-bold rounded-full px-2 py-0.5 border ${
                          a.status === "at_limit" ? "bg-red-50 text-red-700 border-red-200"
                            : a.status === "near_limit" ? "bg-amber-50 text-amber-700 border-amber-200"
                            : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
                          {a.status === "at_limit" ? "Limit reached" : a.status === "near_limit" ? "Near limit" : "OK"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
            <Panel title="Recent signups" testId="recent-signups">
              {data.recent_signups.length === 0 ? (
                <Empty>No signups yet.</Empty>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
                      <th className="text-left py-2 pl-1 font-semibold">User</th>
                      <th className="text-left py-2 font-semibold">Plan</th>
                      <th className="text-left py-2 font-semibold">Change</th>
                      <th className="text-right py-2 pr-1 font-semibold">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_signups.map((u) => (
                      <tr key={u.user_id} className="border-b border-slate-100 last:border-0">
                        <td className="py-2.5 pl-1 max-w-[180px]">
                          <div className="font-medium text-slate-900 truncate">{u.name || u.email}</div>
                          <div className="text-xs text-slate-500 truncate">{u.email}</div>
                        </td>
                        <td className="py-2.5">
                          <PlanBadge plan={u.plan || "free"} size="sm" />
                        </td>
                        <td className="py-2.5">
                          <select
                            data-testid={`plan-select-${u.user_id}`}
                            value={u.plan || "free"}
                            disabled={busyUserId === u.user_id}
                            onChange={(e) => changePlan(u.user_id, e.target.value)}
                            className="text-xs bg-white border border-slate-200 rounded-lg px-2 py-1 hover:border-slate-300 focus:border-[#1D6AFF] outline-none transition-colors disabled:opacity-60"
                          >
                            <option value="free">Free</option>
                            <option value="pro">Pro</option>
                            <option value="elite">Elite</option>
                          </select>
                        </td>
                        <td className="py-2.5 pr-1 text-right text-xs text-slate-500 whitespace-nowrap">
                          {formatTimeAgo(u.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panel>

            <Panel title="Recent payments" testId="recent-payments">
              {data.recent_payments.length === 0 ? (
                <Empty>No payments yet.</Empty>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
                      <th className="text-left py-2 pl-1 font-semibold">Email</th>
                      <th className="text-left py-2 font-semibold">Plan</th>
                      <th className="text-right py-2 font-semibold">Amount</th>
                      <th className="text-right py-2 pr-1 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_payments.map((t) => (
                      <tr key={t.session_id} className="border-b border-slate-100 last:border-0">
                        <td className="py-2.5 pl-1 text-slate-700 truncate max-w-[180px]">{t.email || "—"}</td>
                        <td className="py-2.5 text-slate-700 text-xs">{t.plan_id || "—"}</td>
                        <td className="py-2.5 text-right text-slate-900 font-semibold">${Number(t.amount || 0).toFixed(0)}</td>
                        <td className="py-2.5 pr-1 text-right">
                          <StatusPill status={t.payment_status || t.status || "pending"} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panel>
          </div>
        </>
      )}
    </div>
  );
}

const MetricCard = ({ testId, icon: Icon, label, value, accent, iconBg }) => (
  <div
    data-testid={testId}
    className={`bg-gradient-to-br ${accent} border border-slate-200 rounded-2xl p-5 shadow-sm`}
  >
    <div className="flex items-center gap-3 mb-3">
      <div className={`w-10 h-10 rounded-xl ${iconBg} flex items-center justify-center`}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
    <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">{label}</div>
    <div className="font-display text-3xl font-bold text-slate-900">{value}</div>
  </div>
);

const PlanBucket = ({ label, value, accent, testId }) => (
  <div data-testid={testId} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm flex items-center justify-between">
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">{label}</div>
      <div className="font-display text-2xl font-bold text-slate-900">{value}</div>
    </div>
    <div className={`text-xs font-semibold px-3 py-1.5 rounded-full ${accent}`}>{label}</div>
  </div>
);

const Panel = ({ title, testId, children }) => (
  <div data-testid={testId} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
    <h2 className="font-display text-lg font-semibold text-slate-900 mb-4">{title}</h2>
    {children}
  </div>
);

const Empty = ({ children }) => (
  <div className="text-center py-8 text-sm text-slate-500">{children}</div>
);

const StatusPill = ({ status }) => {
  const map = {
    paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
    complete: "bg-emerald-50 text-emerald-700 border-emerald-200",
    unpaid: "bg-amber-50 text-amber-700 border-amber-200",
    initiated: "bg-slate-50 text-slate-700 border-slate-200",
    expired: "bg-slate-50 text-slate-700 border-slate-200",
    failed: "bg-red-50 text-red-700 border-red-200",
  };
  const cls = map[status] || "bg-slate-50 text-slate-700 border-slate-200";
  return <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${cls}`}>{status}</span>;
};

function formatTimeAgo(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  if (diff < 7 * 86_400_000) return `${Math.floor(diff / 86_400_000)}d ago`;
  return new Date(iso).toLocaleDateString();
}
